import React, {useContext, useRef, useState} from "react";
import PagedSearchTable, {PagedTableFunctions} from "../components/PagedSearchTable";
import {showSuccessOrFailed} from "../Snacks";
import AppContext from "../appContext";
import Originate from "../controllers/Originate";
import Agent from "../controllers/Agent";
import AgentController from "../controllers/AgentController";
import {EditRow, EditTable} from "../components/Fields";
import Input from "../components/Input";
import Dialog from "../components/Dialog";
import {useStateSetter} from "../immutableState";
import {useValidation} from "../validation";
import {wrapLoader} from "../wrapper";
import SelectNumberNullable from "../components/SelectNumberNullable";

const emptyAgent: Agent = {
    id: 0,
    name: '',
    archived: false,
    originate: Originate.International
}

const ManageAgents: React.FC = (props) => {
    const pagedTableRef = useRef<PagedTableFunctions<Agent>>()
    const [popupModal, setPopupModal] = useState<boolean>(false)
    const [data, setData, updateData] = useStateSetter<Agent>(emptyAgent)
    const [assignAgent, setAssignAgent] = useState<Agent | null>(null)
    const [agents, setAgents] = useState<Agent[]>()
    const [newAgentId, setNewAgentId] = useState<number | null>(null)


    const context = useContext(AppContext)

    const validation = useValidation({
        name: () => data.name != null && data.name.length > 0,
    });

    const validationDelete = useValidation({
        reassigned: () => newAgentId !== null
    })
    function deleteAgent(){
        if (!validationDelete.validate() || assignAgent == null || newAgentId == null) return;
        
        showSuccessOrFailed(context, AgentController.archive({id: assignAgent.id, assignId: newAgentId})).then(() => {
            cancelAgentAssign()
            pagedTableRef.current?.refresh();
        })
    }

    function newAgent() {
        setData(emptyAgent)
        setPopupModal(true)
    }

    function editAgent(agent: Agent) {
        setData(agent)
        setPopupModal(true)
    }

    function upsert() {
        if (!validation.validate()) return;

        showSuccessOrFailed(context, AgentController.upsert({id: data.id, name: data.name ?? ""}))
            .then(() => {
                pagedTableRef.current?.refresh();
                setPopupModal(false);
            })
    }

    function cancelAgentAssign() {
        setAssignAgent(null)
        setNewAgentId(null)
    }

    function agentAssign(agent: Agent) {
        wrapLoader(context, AgentController.index(), resp => {
            setAgents(resp)
            setAssignAgent(agent)
        })
    }

    return <div>
        <div className="pb-2">
            <div className="btn bg-primary" onClick={newAgent}>Add Agent</div>
        </div>
        <PagedSearchTable componentRef={pagedTableRef}
                          call={AgentController.paged}
                          columns={[
                              {
                                  header: 'Agent',
                                  key: 'agronomist',
                                  row: (row) => row.name
                              },
                              {
                                  header: <div className='text-right'>Actions</div>,
                                  key: 'actions',
                                  row: (row, index) => <div className='text-right'>
                                      <div className="bg-primary btn-sm"
                                           onClick={() => editAgent(row)}>
                                          edit
                                      </div>
                                      <div className="bg-red-500 btn-sm"
                                           onClick={() => agentAssign(row)}>x
                                      </div>
                                  </div>
                              }
                          ]}/>
        
        <Dialog title={"Agent"}
                show={popupModal}
                setShow={setPopupModal}
                body={<>
                    <EditTable discard={() => setPopupModal(false)} save={() => upsert()}
                               saveWord={data.id === 0 ? 'insert' : 'update'}>
                        {EditRow('Name', <Input value={data.name ?? ''}
                                                change={v => updateData({name: v})}/>, validation.rules.name, 'Name required')}
                    </EditTable>
                </>}/>

        <Dialog title={`Assign ${assignAgent?.name ? assignAgent.name : "this agent"} to New Agent`}
                show={assignAgent != null}
                setShow={cancelAgentAssign}
                body={<div>
                    <EditTable discard={() => cancelAgentAssign()} 
                               save={() => deleteAgent()}
                               saveWord={'ReAssign'}>
                        {agents !== null && agents ? EditRow('Agent', <div className='text-right'>
                                <SelectNumberNullable options={agents.filter(a => a.id !== assignAgent?.id)}
                                                      textFunc={t => t.name ?? ""}
                                                      valueFunc={v => v.id}
                                                      value={newAgentId}
                                                      onChange={v => setNewAgentId(v)}/>
                            </div>,
                            validationDelete.rules.reassigned,
                            'Select a agent to reassign'
                        ) : null}
                    </EditTable>
                </div>}/>
    </div>
}

export default ManageAgents;